/**
 * Overview:
 * This file defines shared types, utility functions, and widget mappings
 * used to power the dashboard's dynamic widget system. It includes data
 * models for calendar events and out-of-office records, a safe lazy-loading
 * mechanism for on-demand component loading, and a centralized registry of
 * dashboard widgets that supports configurable layouts and improved
 * application performance through code splitting.
 */
import React from "react";

export interface CalendarEvent {
  quarter: string;
  reviewDate: string; // YYYY-MM-DD
  prereadDeadline: string;
  platform: string;
  partnership: string;
  pointPerson: string;
  budget: number | null;
  notes: string;
}

export interface OOO {
  name: string;
  startDate: string;
  endDate: string;
  reason: string;
}
/**
 *  Safe Lazy Loader
 */
const safeLazy = (importFn: any, exportName?: string) =>
  React.lazy(async () => {
    const module = await importFn();

    if (exportName) {
      if (!module[exportName]) {
        throw new Error(`Component ${exportName} not found`);
      }
      return { default: module[exportName] };
    }

    if (!module.default) {
      throw new Error("Default export not found");
    }

    return { default: module.default };
  });

export type WidgetConfig = {
  component: keyof typeof widgetComponentMap;
  layout: string;
  title?: string;
};

export type WidgetProps = {
  userUpn: string;
};
/**
 * Component Map
 */
export const widgetComponentMap: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<WidgetProps>>
> = {
  QuickAccessApps: safeLazy(() => import("@/Components/Widgets/QuickAccessApps")),
  ActiveProgramsWidget: safeLazy(() => import("@/features/ActivePrograms"), "ActiveProgramsWidget"),
  TaskPriorities: safeLazy(() => import("@/Components/Widgets/TaskPriorities"), "TaskPriorities"),
  NotificationsPanel: safeLazy(
    () => import("@/features/Notifications/component/NotificationsPanel"),
    "NotificationsPanel"
  ),
  KeyGuidelines: safeLazy(() => import("@/Components/Widgets/KeyGuidelines"), "KeyGuidelines"),
  MeetingsWidget: safeLazy(
    () => import("@/features/Upcoming-meetings/MeetingsWidget"),
    "MeetingsWidget"
  ),
  ImpactTargets: safeLazy(
    () => import("@/features/ImpactTargets/components/ImpactTargets"),
    "ImpactTargets"
  ),
  TeamCalendarWidget: safeLazy(
    () => import("@/Components/TeamCalendar/TeamCalendarWidget"),
    "TeamCalendarWidget"
  ),
  PartnersDirectory: safeLazy(() => import("@/Components/Widgets/partnersDirectory")),
  partnerHub : safeLazy(() => import("@/Components/Widgets/partnerHub")),
  LearningInsights: safeLazy(() => import("@/Components/Widgets/LearningInsights")),
  AnalyticsWidget: safeLazy(() => import("@/features/AnalyticsInsights/component/AnalyticsWidget")),
  // CompassWidget: safeLazy(() => import("@/backup/Compass/CompassWidget")),
  // PartnerIssuesRisksWidget: safeLazy(
  //   () => import("@/backup/partnerIssuesRisks/PartnerIssuesRisksWidget"),
  //   "PartnerIssuesRisksWidget"
  // ),
  // ProgramMetrics: safeLazy(() => import("@/backup/programMetrics/component/ProgramMetrics")),
  ProgramResources: safeLazy(
    () => import("@/Components/Widgets/ProgramResources"),
    "ProgramResources"
  ),
  OversightWidget: safeLazy(() => import("@/Components/Oversightwidget/OversightWidget")),
  PipelinePriority: safeLazy(() => import("@/features/PipelinePriority/PipelinePriority")),
  // SubmissionsWidget: safeLazy(
  //   () => import("@/backup/Submission/SubmissionsWidget"),
  //   "SubmissionsWidget"
  // ),
  // PaymentsPipelineWidget: safeLazy(
  //   () => import("@/backup/PaymentsPipelineWidget"),
  //   "PaymentsPipelineWidget"
  // ),
  PartnerSubmissions: safeLazy(
    () => import("@/features/partnerSubmission/component/PartnerSubmissions")
  ),
  DisbursementRequestsWrapper: safeLazy(
    () => import("@/Components/Widgets/DisbursementRequestsWrapper")
  ),
  WorldClock: safeLazy(() => import("@/Components/TimezoneBar")),
  Accessibility: safeLazy(() => import("@/features/AccessibilityPanel/AccessibilityPanel")),
  AccountSettings: safeLazy(() => import("@/features/Navbar/components/ProfileModal")),
  IdeasTracker: safeLazy(() => import("@/Components/IdeasTracker")),
  ProgramImpact: safeLazy(() => import("@/Components/Widgets/ProgramImpact")),
};
